/**
 * @license
 * Copyright Akveo. All Rights Reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 */
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { ApplicationRef, DoBootstrap, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import {
  NbDatepickerModule,
  NbDialogModule,
  NbMenuModule,
  NbSidebarModule,
  NbToastrModule,
  NbWindowModule,
} from '@nebular/theme';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { NgxEchartsModule } from 'ngx-echarts';
import { LoggerModule, NGXLogger } from 'ngx-logger';
import { from, interval } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { CoreModule } from './@core/core.module';
import { ThemeModule } from './@theme/theme.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { appReducer } from './app.state';
import { GraphQLModule } from './graphql.module';
import { COMPONENTS_LIST } from './models/componentsList';
import { LoadingService } from './services/loading/loading.service';
import { ProspectusService } from './services/prospectus.service';
import * as fromStore from './store';
import { ReactiveFormsModule } from '@angular/forms';
import { MathJaxModule } from 'ngx-mathjax';

const keycloakService = new KeycloakService();
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}
@NgModule({
  declarations: [AppComponent],
  imports: [
    KeycloakAngularModule,
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    GraphQLModule,
    AppRoutingModule,
    LoggerModule.forRoot({
      level: environment.logLevel,
      colorScheme: [
        'purple',
        'purple',
        'green',
        'black',
        'yellow',
        'red',
        'red',
      ],
    }),
    StoreModule.forRoot(appReducer),
    EffectsModule.forRoot([
      fromStore.UserEffects,
      fromStore.SensorNodesEffects,
      fromStore.SensorsEffects,
      fromStore.NotificationsEffects,
    ]),
    StoreDevtoolsModule.instrument({
      maxAge: 25, // Retains last 25 states
      logOnly: environment.production, // Restrict extension to log-only mode
    }),
    NbSidebarModule.forRoot(),
    NbMenuModule.forRoot(),
    NbDatepickerModule.forRoot(),
    NbDialogModule.forRoot(),
    NbWindowModule.forRoot(),
    NbToastrModule.forRoot(),
    CoreModule.forRoot(),
    ThemeModule.forRoot(),
    NgxEchartsModule.forRoot({
      echarts: () => import('echarts'),
    }),
    ReactiveFormsModule,
    MathJaxModule.forRoot({
      version: '2.7.5',
      config: 'TeX-AMS_HTML',
      hostname: 'cdnjs.cloudflare.com',
    }),
  ],
  providers: [
    {
      provide: KeycloakService,
      useValue: keycloakService,
    },
    ProspectusService,
  ],
  exports: [],
  entryComponents: [AppComponent],
})
export class AppModule implements DoBootstrap {
  constructor(
    private router: Router,
    private logger: NGXLogger,
    private loadingService: LoadingService,
  ) {}
  ngDoBootstrap(appRef: ApplicationRef) {
    if (!environment.keycloak.enabled) {
      appRef.bootstrap(AppComponent);
    } else {
      let keycloakInit = keycloakService
        .init({
          config: environment.keycloak.config,
          initOptions: {
            onLoad: 'login-required',
            silentCheckSsoRedirectUri:
              window.location.origin + '/assets/silent-check-sso.html',
            checkLoginIframe: false,
            redirectUri: environment.keycloak.postLoginUrl,
          },
          loadUserProfileAtStartUp: true,
          // enableBearerInterceptor: true,
          bearerExcludedUrls: ['/assets/test.json'],
        })
        .then(() => {
          this.logger.debug('[ngDoBootstrap] bootstrap app');
          // interval to refresh keycloak token
          interval(environment.keycloak.refreshTokenIntervalMS).subscribe(() =>
            keycloakService
              .updateToken()
              // the token will refresh only if it's expired
              .then((value) => {
                if (value) {
                  // Reset if state is back to normal
                  //this.loadingService.removeFatalErrors();
                  this.logger.debug('Keycloak token successfully refreshed');
                  keycloakService
                    .getToken()
                    .then((token) =>
                      localStorage.setItem('access_token', token),
                    );
                } else {
                  this.logger.error(
                    'Error refreshing Keycloak token: token is still valid',
                  );
                }
              })
              .catch((e) =>
                this.logger.error('Error refreshing Keycloak token: ', e),
              ),
          );
          appRef.bootstrap(AppComponent);
        })
        .catch((error) => {
          const message = '[ngDoBootstrap] init Keycloak failed ' + error;
          this.loadingService.addFatalError({
            type: 'AUTHENTICATION',
            message,
          });
          console.error(message);
        });
      let keycloakInit$ = from(keycloakInit);
      keycloakInit$.pipe(timeout(environment.keycloak.initTimeout)).subscribe(
        (v) => {},
        (e) => {
          const err =
            'Unable to establish a connection with the Identity and Access Manager';
          this.loadingService.addWarning({
            component: COMPONENTS_LIST.APP,
            err,
          });
          console.error(err);
          appRef.bootstrap(AppComponent);
          this.router.navigateByUrl('/error');
        },
      );
    }
  }
}
